import { LogoFull } from "@/components/Logo";
import { scrollToSection } from "@/lib/api";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Download, MapPin, Sparkles } from "lucide-react";
import { requestInstall } from "@/components/InstallPrompt";
import { openAiChat } from "@/components/ai/AiChat";
import { LEGAL_BUSINESS } from "@/lib/legalContent";
import { DELIVERY_MESSAGE, INTERNATIONAL_ORDER_NOTE } from "@/lib/deliveryContent";

export default function Footer() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const go = (id) => pathname === "/" ? scrollToSection(id) : navigate(`/#${id}`);
  return (
    <footer id="contact" className="relative overflow-hidden bg-ragi-dark pt-20 text-cream" data-testid="site-footer">
      <div className="relative z-10 mx-auto max-w-7xl px-4 sm:px-8">
        <div className="grid gap-12 pb-16 md:grid-cols-3">
          <div>
            <div className="rounded-2xl bg-cream/95 inline-flex p-3">
              <LogoFull compact testId="footer-brand-logo" />
            </div>
            <p className="mt-5 max-w-xs text-sm leading-relaxed text-cream/70">
              Sprouted millet protein powders, protein laddus, artisanal chocolates and
              traditional halwas — Made with Love by Kandulas.
            </p>
          </div>
          <div>
            <p className="mb-4 font-mono text-xs uppercase tracking-[0.22em] text-saffron">Explore</p>
            <ul className="space-y-3 text-sm">
              {[
                ["Shop the catalog", "catalog"],
                ["Why Kandula", "why-us"],
                ["Our story", "story"],
                ["Bulk enquiry", "enquiry"],
              ].map(([label, id]) => (
                <li key={id}>
                  <button onClick={() => go(id)} className="text-cream/75 transition-colors hover:text-saffron" data-testid={`footer-${id}-link`}>
                    {label}
                  </button>
                </li>
              ))}
              <li><Link to="/legal" className="text-cream/75 transition-colors hover:text-saffron" data-testid="footer-legal-link">Terms & conditions</Link></li>
            </ul>
          </div>
          <div>
            <p className="mb-4 font-mono text-xs uppercase tracking-[0.22em] text-saffron">Reach Us</p>
            <ul className="space-y-3 text-sm text-cream/75">
              <li data-testid="footer-whatsapp"><Link to="/contact" onClick={(event) => { if (pathname === "/contact") { event.preventDefault(); scrollToSection("contact-top"); } }} className="transition-colors hover:text-saffron" data-testid="footer-contact-link">Contact & WhatsApp enquiries</Link></li>
              <li className="flex items-start gap-2 text-cream" data-testid="footer-business-location"><MapPin className="mt-0.5 h-4 w-4 shrink-0 text-saffron" aria-hidden="true" /><span>Kandula Home Foods · Tanuku</span></li>
              <li className="leading-relaxed text-cream" data-testid="footer-delivery-location">{DELIVERY_MESSAGE}</li>
              <li className="text-xs leading-relaxed" data-testid="footer-international-order-note">{INTERNATIONAL_ORDER_NOTE}</li>
              <li className="text-xs" data-testid="footer-full-location">{LEGAL_BUSINESS.location}</li>
              <li data-testid="footer-fssai-registration">
                <span className="block">FSSAI Licence / Regd. No.:</span>
                <span className="block font-mono text-sm text-cream" data-testid="footer-fssai-number">{LEGAL_BUSINESS.fssai}</span>
              </li>
              <li><Link to="/account/orders" className="text-cream/75 transition-colors hover:text-saffron" data-testid="footer-my-orders-link">My orders</Link></li>
            </ul>
            <div className="mt-6 flex flex-wrap gap-2">
              <button onClick={requestInstall} className="inline-flex items-center gap-2 rounded-full bg-saffron px-4 py-2 font-mono text-[11px] font-semibold uppercase tracking-[0.14em] text-ragi-dark transition-transform hover:scale-[1.03]" data-testid="footer-install-app-button"><Download className="h-3.5 w-3.5" /> Get the app</button>
              <button onClick={() => openAiChat()} className="inline-flex items-center gap-2 rounded-full border border-cream/30 px-4 py-2 font-mono text-[11px] font-semibold uppercase tracking-[0.14em] text-cream transition-colors hover:border-saffron hover:text-saffron" data-testid="footer-ask-ai-button"><Sparkles className="h-3.5 w-3.5" /> Ask Kandula AI</button>
            </div>
          </div>
        </div>
        <div className="flex flex-col items-center justify-between gap-3 border-t border-cream/10 py-6 text-xs text-cream/50 sm:flex-row">
          <p data-testid="footer-copyright">© {new Date().getFullYear()} Kandula Home Foods. All rights reserved.</p>
          <p className="font-mono uppercase tracking-[0.18em]">Made with Love by Kandulas</p>
        </div>
      </div>
      <p
        aria-hidden="true"
        className="pointer-events-none select-none text-center font-display text-[19vw] font-bold leading-[0.75] text-cream/[0.05]"
      >
        KANDULA
      </p>
    </footer>
  );
}
